<template>
    <div class="modal" @click="$emit('close')">
        <div class="modal-content" @click.stop>
            <div class="modal-header">
                <h3>Check-in Heat Map</h3>
                <button class="close-btn" @click="$emit('close')">&times;</button>
            </div>

            <div class="tabs">
                <button 
                    v-for="tab in tabs" 
                    :key="tab.id"
                    :class="['tab-btn', { active: activeTab === tab.id }]"
                    @click="activeTab = tab.id"
                >
                    {{ tab.label }}
                </button>
            </div>

            <div class="scatter-plot-container">
                <div class="scatter-plot">
                    <!-- Grid lines -->
                    <div class="grid-lines">
                        <div class="grid-line" style="bottom: 50%"></div>
                    </div>
                    <div 
                        v-for="score in currentScores" 
                        :key="score.member_id"
                        class="member-circle"
                        :style="getCirclePosition(score[currentScoreType])"
                    >
                        <img 
                            v-if="score.photo_url"
                            :src="score.photo_url"
                            :alt="`${score.first_name} ${score.last_name}`"
                            class="member-photo"
                        />
                        <span v-else>{{ getInitials(`${score.first_name} ${score.last_name}`) }}</span>
                        <div class="member-name-tooltip">
                            {{ score.display_name || score.first_name }}
                        </div>
                    </div>
                    <!-- Y-axis -->
                    <div class="y-axis">
                        <div v-for="n in 10" :key="n" class="y-tick">
                            <div class="y-tick-line"></div>
                            {{ 11-n }}
                        </div>
                    </div>
                    <!-- X-axis -->
                    <div class="x-axis"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { checkinService } from '../services/checkinService';

export default {
    name: 'HeatMapModal',
    props: {
        meeting: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const activeTab = ref('business');
        const checkInData = ref([]);

        const tabs = [
            { id: 'business', label: 'Business' },
            { id: 'health', label: 'Health' },
            { id: 'personal', label: 'Personal' }
        ];

        const currentScoreType = computed(() => `${activeTab.value}_score`);

        const currentScores = computed(() => {
            return [...checkInData.value].sort((a, b) => 
                b[currentScoreType.value] - a[currentScoreType.value]
            );
        });

        const getCirclePosition = (score) => {
            const yPosition = (score / 10) * 100;
            const xPosition = 10 + Math.random() * 80;
            return {
                position: 'absolute',
                bottom: `${yPosition}%`,
                left: `${xPosition}%`,
                backgroundColor: score >= 7 ? '#4CAF50' : 
                               score >= 4 ? '#FFC107' : '#F44336'
            };
        };

        const getInitials = (name) => {
            if (!name) return '??';
            return name.split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase();
        };

        const loadCheckInData = async () => {
            try {
                const data = await checkinService.getEventCheckIns(props.meeting.id);
                checkInData.value = data;
            } catch (error) {
                console.error('Error loading check-in data:', error);
            }
        };

        onMounted(loadCheckInData);

        return {
            activeTab,
            tabs,
            currentScoreType,
            currentScores,
            getCirclePosition,
            getInitials
        };
    }
};
</script>

<style scoped>
.modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 0;
    overflow-y: auto;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 900px;
    max-height: calc(100vh - 4rem);
    overflow-y: auto;
    position: relative;
}

/* Mobile Styles */
@media (max-width: 768px) {
    .modal-content {
        padding: 1rem;
        border-radius: 0;
        height: 100vh;
        max-height: none;
    }

    .scatter-plot {
        transform-origin: top left;
        transform: scale(0.8);
        margin-right: -20%;
    }

    .member-circle {
        width: 40px;
        height: 40px;
    }

    .scatter-plot-container {
        height: calc(70vh - 120px);
        padding: 0.5rem;
        padding-left: 1.5rem;
    }

    .tabs {
        justify-content: center;
        flex-wrap: wrap;
    }

    .tab-btn {
        font-size: 0.9rem;
        padding: 0.4rem 0.8rem;
    }

    .close-btn {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        padding: 0.5rem;
        font-size: 1rem;
        background: #f0f0f0;
        border-radius: 4px;
    }

    h2 {
        font-size: 1.5rem;
        margin-bottom: 1.5rem;
        text-align: center;
    }

    .category-filters {
        display: flex;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
        flex-wrap: wrap;
    }

    .filter-btn {
        padding: 0.5rem 1rem;
        font-size: 0.9rem;
    }
}

/* Small phones */
@media (max-width: 375px) {
    .scatter-plot {
        transform: scale(0.7);
        margin-right: -30%;
    }

    .member-circle {
        width: 35px;
        height: 35px;
    }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.close-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    color: #666;
}

.close-btn:hover {
    color: #000;
}

.tabs {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
}

.tab-btn {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    background: #f0f0f0;
}

.tab-btn.active {
    background: #4285f4;
    color: white;
}

.scatter-plot-container {
    position: relative;
    width: 100%;
    height: calc(80vh - 120px);
    padding: 1rem;
    padding-left: 2rem;
}

.scatter-plot {
    position: relative;
    width: 100%;
    height: 100%;
    border-left: 2px solid #ccc;
    border-bottom: 2px solid #ccc;
    margin: 0 2rem;
}

.member-circle {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 500;
    transition: all 0.3s ease;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    z-index: 1;
}

.member-photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 50%;
    overflow: hidden;
}

.member-circle > img,
.member-circle > span {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: inherit;
    font-size: 1rem;
    font-weight: 500;
}

.member-circle > img {
    background-color: transparent;
}

.y-axis {
    position: absolute;
    left: -30px;
    top: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    font-size: 1rem;
}

.y-tick {
    color: #666;
    font-size: 1rem;
    position: relative;
    display: flex;
    align-items: center;
    padding-right: 10px;
}

.y-tick-line {
    position: absolute;
    right: -12px;
    width: 6px;
    height: 2px;
    background-color: #666;
}

.grid-lines {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.grid-line {
    position: absolute;
    width: 100%;
    height: 1px;
    background-color: rgba(102, 102, 102, 0.2);
}

.x-axis {
    position: absolute;
    bottom: -20px;
    left: 0;
    width: 100%;
    height: 2px;
}

.member-name-tooltip {
    position: absolute;
    left: 50%;
    top: 100%;
    transform: translateX(-50%);
    padding: 4px 8px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    border-radius: 4px;
    font-size: 1rem;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
    margin-top: 5px;
    z-index: 10;
}

.member-circle:hover {
    z-index: 100;
}

.member-circle:hover .member-name-tooltip {
    opacity: 1;
}
</style> 